import {useEffect,useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@/components/ui/Button';
import {Input,FieldLabel} from '@/components/ui/Input';
import {ErrorState,LoadingState} from '@/components/ui/States';
import {usePriceTiers,useSetPriceTiers} from '@/api/hooks';
import {getLocalizedError} from '@/lib/api';
import {parseMoneyMajor} from '@/lib/moneyInput';
import {unitPriceToInput} from '@/domain/money';

/**
 * Bậc giá bán theo số lượng — chung mọi khách, chỉ giá bán.
 *
 * Chỉ nhập "TỪ số lượng", không nhập "đến": mốc kế tiếp tự khép bậc trước, nên
 * không bao giờ có khoảng hở hay chồng lấn giữa 100 và 101. Màn hình hiện lại
 * khoảng suy ra để người nhập vẫn đọc được "1–100", "101–200".
 *
 * KHÔNG đụng EXW/DTD/giá vốn, và KHÔNG thay ô giá dưới MOQ: bậc là giá sỉ khi
 * đã đạt MOQ, giá dưới MOQ là cho đơn nhỏ.
 */
type Row = {fromQuantity:string;price:string};

export function PriceTiersSection({productId,currency,canSet}:{productId:string;currency:string;canSet:boolean}){
 const {t}=useTranslation();
 const tiers=usePriceTiers(productId);
 const save=useSetPriceTiers(productId);
 const [rows,setRows]=useState<Row[]>([]);
 const [error,setError]=useState<string|null>(null);
 useEffect(()=>{
  // Số trần cho ô nhập: nạp "$0.0122" (có ký hiệu) thì parseMoneyMajor từ chối và bậc đã lưu không lưu lại được nữa.
  if(tiers.data)setRows(tiers.data.map(r=>({fromQuantity:String(r.fromQuantity),price:unitPriceToInput(r.price)})));
 },[tiers.data]);
 if(tiers.isLoading)return <LoadingState/>;
 if(tiers.error)return <ErrorState message={t('priceTiers.error')} onRetry={()=>void tiers.refetch()}/>;

 const sorted=[...rows].map((r,i)=>({...r,i})).sort((a,b)=>Number(a.fromQuantity||0)-Number(b.fromQuantity||0));
 const upTo=(i:number)=>{const pos=sorted.findIndex(r=>r.i===i);const next=sorted[pos+1];return next?String(Number(next.fromQuantity)-1):t('priceTiers.andUp');};

 const submit=async()=>{
  setError(null);
  const parsed:{fromQuantity:number;price:number}[]=[];
  for(const r of rows){
   const from=Number(r.fromQuantity);
   const money=parseMoneyMajor(r.price);
   if(!Number.isInteger(from)||from<1||money.invalid||money.minor==null){setError(t('priceTiers.invalid'));return;}
   parsed.push({fromQuantity:from,price:money.minor});
  }
  if(new Set(parsed.map(p=>p.fromQuantity)).size!==parsed.length){setError(t('priceTiers.duplicate'));return;}
  try{await save.mutateAsync({tiers:parsed});}catch(e){setError(getLocalizedError(e,t,'priceTiers.error'));}
 };

 return <section className="space-y-3">
  <div>
   <h2 className="font-semibold">{t('priceTiers.title')}</h2>
   <p className="mt-1 text-xs text-ink-soft">{t('priceTiers.intro')}</p>
  </div>
  {rows.length===0&&<p className="text-sm text-ink-soft">{t('priceTiers.empty')}</p>}
  {rows.map((r,i)=><div key={i} className="flex flex-wrap items-end gap-2">
   <div className="w-32">
    <FieldLabel htmlFor={`tier-from-${i}`}>{t('priceTiers.fromQuantity')}</FieldLabel>
    <Input id={`tier-from-${i}`} className="font-mono" inputMode="numeric" disabled={!canSet} value={r.fromQuantity}
      onChange={e=>setRows(v=>v.map((x,j)=>j===i?{...x,fromQuantity:e.target.value}:x))}/>
   </div>
   <div className="w-36">
    <FieldLabel htmlFor={`tier-price-${i}`}>{t('priceTiers.price',{currency})}</FieldLabel>
    <Input id={`tier-price-${i}`} className="font-mono" inputMode="decimal" disabled={!canSet} value={r.price}
      onChange={e=>setRows(v=>v.map((x,j)=>j===i?{...x,price:e.target.value}:x))}/>
   </div>
   <p className="pb-2 text-xs text-ink-soft">{t('priceTiers.range',{from:r.fromQuantity||'—',to:upTo(i)})}</p>
   {canSet&&<Button size="sm" variant="ghost" onClick={()=>setRows(v=>v.filter((_,j)=>j!==i))}>{t('common.remove')}</Button>}
  </div>)}
  {canSet&&<div className="flex flex-wrap gap-2">
   <Button size="sm" variant="secondary" onClick={()=>setRows(v=>[...v,{fromQuantity:'',price:''}])}>{t('priceTiers.add')}</Button>
   <Button size="sm" disabled={save.isPending} onClick={()=>void submit()}>{t('common.save')}</Button>
  </div>}
  {error&&<p role="alert" className="text-sm text-danger">{error}</p>}
 </section>;
}
